/**
 * Sport-agnostic gaze configuration registry.
 * Each sport defines roles, max players, gaze phases, and event vocabularies.
 */

const SPORT_CONFIGS = {
  basketball: {
    label: 'Basketball',
    maxPlayers: 10,
    roles: ['point_guard', 'shooting_guard', 'small_forward', 'power_forward', 'center'],
    phases: [
      { id: 'offense', label: 'Offense', color: '#60A5FA' },
      { id: 'defense', label: 'Defense', color: '#34D399' },
      { id: 'transition', label: 'Transition', color: '#FBBF24' },
      { id: 'free_throw', label: 'Free Throw', color: '#A78BFA' },
      { id: 'inbound', label: 'Inbound', color: '#F472B6' },
    ],
    events: {
      'B-01': { label: 'Court Vision Scan', severity: 'info', color: '#60A5FA' },
      'B-02': { label: 'Shooter Lock', severity: 'alert', color: '#C0392B' },
      'B-03': { label: 'Help Defense Read', severity: 'info', color: '#34D399' },
      'B-04': { label: 'Ball Screen Read', severity: 'info', color: '#FBBF24' },
      'B-05': { label: 'Rim Focus', severity: 'info', color: '#FFFFFF' },
      'B-06': { label: 'Defensive Ball Watch', severity: 'warning', color: '#FF6B6B' },
      'B-07': { label: 'Lost Assignment Gaze', severity: 'alert', color: '#FF6B6B' },
      'B-08': { label: 'Bench/Clock Distraction', severity: 'warning', color: '#A78BFA' },
    },
  },

  baseball: {
    label: 'Baseball',
    maxPlayers: 10,
    roles: ['hitter', 'pitcher', 'catcher', 'infielder', 'outfielder'],
    phases: [
      { id: 'P1', label: 'Pre-Pitch', color: '#60A5FA' },
      { id: 'P2', label: 'Wind-Up', color: '#34D399' },
      { id: 'P3', label: 'Tracking', color: '#FBBF24' },
      { id: 'P4', label: 'Swing Decision', color: '#A78BFA' },
      { id: 'P5', label: 'Contact/Follow', color: '#F472B6' },
    ],
    events: {
      'H-01': { label: 'Pre-Pitch Focal Lock', severity: 'info', color: '#60A5FA' },
      'H-02': { label: 'Release Point Trigger', severity: 'info', color: '#34D399' },
      'H-03': { label: 'Smooth Pursuit Initiation', severity: 'info', color: '#FBBF24' },
      'H-04': { label: 'Anticipatory Saccade', severity: 'alert', color: '#C0392B' },
      'H-05': { label: 'Zone Fixation (Contact)', severity: 'info', color: '#FFFFFF' },
      'H-06': { label: 'Early Head Bail', severity: 'alert', color: '#FF6B6B' },
      'H-07': { label: 'Zone Chase Pattern', severity: 'alert', color: '#FF6B6B' },
      'H-08': { label: 'Two-Strike Gaze Shift', severity: 'warning', color: '#A78BFA' },
    },
  },

  football: {
    label: 'Football',
    maxPlayers: 22,
    roles: ['quarterback', 'receiver', 'running_back', 'lineman', 'linebacker', 'defensive_back', 'safety'],
    phases: [
      { id: 'pre_snap', label: 'Pre-Snap', color: '#60A5FA' },
      { id: 'snap', label: 'Snap', color: '#34D399' },
      { id: 'route_run', label: 'Route Running', color: '#FBBF24' },
      { id: 'throw_catch', label: 'Throw/Catch', color: '#A78BFA' },
      { id: 'tackle', label: 'Tackle', color: '#F472B6' },
    ],
    events: {
      'F-01': { label: 'Pre-Snap Read', severity: 'info', color: '#60A5FA' },
      'F-02': { label: 'Coverage Scan', severity: 'info', color: '#34D399' },
      'F-03': { label: 'Receiver Lock', severity: 'alert', color: '#C0392B' },
      'F-04': { label: 'Ball Track', severity: 'info', color: '#FBBF24' },
      'F-05': { label: 'Tunnel Vision', severity: 'warning', color: '#FF6B6B' },
    },
  },

  soccer: {
    label: 'Soccer',
    maxPlayers: 22,
    roles: ['goalkeeper', 'defender', 'midfielder', 'forward'],
    phases: [
      { id: 'build_up', label: 'Build Up', color: '#60A5FA' },
      { id: 'attack', label: 'Attack', color: '#34D399' },
      { id: 'defense', label: 'Defense', color: '#FBBF24' },
      { id: 'set_piece', label: 'Set Piece', color: '#A78BFA' },
      { id: 'transition', label: 'Transition', color: '#F472B6' },
    ],
    events: {
      'S-01': { label: 'Pitch Scan', severity: 'info', color: '#60A5FA' },
      'S-02': { label: 'Passing Lane Lock', severity: 'info', color: '#34D399' },
      'S-03': { label: 'Goal Focus', severity: 'alert', color: '#C0392B' },
      'S-04': { label: 'Marking Lost', severity: 'warning', color: '#FF6B6B' },
    },
  },

  hockey: {
    label: 'Hockey',
    maxPlayers: 12,
    roles: ['center', 'wing', 'defenseman', 'goalie'],
    phases: [
      { id: 'offensive_zone', label: 'Offensive Zone', color: '#60A5FA' },
      { id: 'defensive_zone', label: 'Defensive Zone', color: '#34D399' },
      { id: 'neutral_zone', label: 'Neutral Zone', color: '#FBBF24' },
      { id: 'power_play', label: 'Power Play', color: '#A78BFA' },
      { id: 'penalty_kill', label: 'Penalty Kill', color: '#F472B6' },
    ],
    events: {
      'K-01': { label: 'Ice Scan', severity: 'info', color: '#60A5FA' },
      'K-02': { label: 'Shot Lane Lock', severity: 'alert', color: '#C0392B' },
      'K-03': { label: 'Puck Track', severity: 'info', color: '#34D399' },
      'K-04': { label: 'Assignment Lost', severity: 'warning', color: '#FF6B6B' },
    },
  },

  lacrosse: {
    label: 'Lacrosse',
    maxPlayers: 20,
    roles: ['attackman', 'midfielder', 'defenseman', 'goalie'],
    phases: [
      { id: 'settled_offense', label: 'Settled Offense', color: '#60A5FA' },
      { id: 'fast_break', label: 'Fast Break', color: '#34D399' },
      { id: 'ride', label: 'Ride', color: '#FBBF24' },
      { id: 'clear', label: 'Clear', color: '#A78BFA' },
      { id: 'man_down', label: 'Man Down', color: '#F472B6' },
    ],
    events: {
      'L-01': { label: 'Dodge Read', severity: 'info', color: '#60A5FA' },
      'L-02': { label: 'Feed Target Lock', severity: 'alert', color: '#C0392B' },
      'L-03': { label: 'Slide Awareness', severity: 'info', color: '#34D399' },
    },
  },

  general: {
    label: 'General',
    maxPlayers: 20,
    roles: ['player'],
    phases: [
      { id: 'active', label: 'Active', color: '#60A5FA' },
      { id: 'resting', label: 'Resting', color: '#34D399' },
    ],
    events: {
      'G-01': { label: 'Focus Lock', severity: 'info', color: '#60A5FA' },
      'G-02': { label: 'Attention Shift', severity: 'info', color: '#34D399' },
      'G-03': { label: 'Distraction', severity: 'warning', color: '#FF6B6B' },
    },
  },
};

// Player colors for multiplayer overlay (up to 22 players)
const PLAYER_COLORS = [
  '#C0392B', // SLAM Red
  '#60A5FA', // Blue
  '#34D399', // Green
  '#FBBF24', // Gold
  '#A78BFA', // Purple
  '#F472B6', // Pink
  '#FB923C', // Orange
  '#22D3EE', // Cyan
  '#E879F9', // Fuchsia
  '#4ADE80', // Lime
  '#F87171', // Light Red
  '#38BDF8', // Sky
  '#FACC15', // Yellow
  '#A3E635', // Lime Green
  '#2DD4BF', // Teal
  '#C084FC', // Violet
  '#FB7185', // Rose
  '#FCD34D', // Amber
  '#6EE7B7', // Emerald
  '#93C5FD', // Light Blue
  '#D8B4FE', // Lavender
  '#FCA5A1', // Salmon
];

export function getSportConfig(sport) {
  return SPORT_CONFIGS[sport] || SPORT_CONFIGS.general;
}

export function getPlayerColor(playerIndex) {
  return PLAYER_COLORS[playerIndex % PLAYER_COLORS.length];
}

export function getSupportedSports() {
  return Object.entries(SPORT_CONFIGS)
    .filter(([key]) => key !== 'general')
    .map(([key, config]) => ({ id: key, label: config.label }));
}

export { SPORT_CONFIGS, PLAYER_COLORS };
